/**
 * Registre unique des polices de l'éditeur.
 *
 * - `file`  : police hébergée (woff2) -> chargée à la demande via l'API FontFace.
 * - `system`: police système (aucun chargement nécessaire).
 * - `google`: police déjà déclarée dans index.html (aucun chargement nécessaire).
 * - `noBold` / `noItalic` : la police n'existe qu'en graisse/style regular —
 *   l'éditeur désactive le bouton et force le rendu sans faux gras/italique.
 */
export interface FontDef {
  name: string;
  file?: string;
  system?: boolean;
  google?: boolean;
  noBold?: boolean;
  noItalic?: boolean;
}

const REGULAR_ONLY = { noBold: true, noItalic: true } as const;

export const FONTS: FontDef[] = [
  { name: 'Abril Fatface', file: '/fonts/AbrilFatface-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Alfa Slab One', file: '/fonts/AlfaSlabOne-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Allura', file: '/fonts/Allura-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Arial', system: true },
  { name: 'Ballet', file: '/fonts/Ballet.woff2', ...REGULAR_ONLY },
  { name: 'Barlow', file: '/fonts/Barlow-Regular.woff2' },
  { name: 'Comic Sans MS', system: true },
  { name: 'Courier New', system: true },
  { name: 'Cozy Autumns', file: '/fonts/CozyAutumns.woff2', ...REGULAR_ONLY },
  { name: 'DM Sans', file: '/fonts/DMSans-Regular.woff2' },
  { name: 'Georgia', system: true },
  { name: 'Gochi Hand', file: '/fonts/GochiHand-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Handlee', file: '/fonts/Handlee-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Iceland', file: '/fonts/Iceland-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Impact', system: true, ...REGULAR_ONLY },
  { name: 'Keania One', file: '/fonts/KeaniaOne-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Knewave', file: '/fonts/Knewave-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Lato', file: '/fonts/Lato-Regular.woff2' },
  { name: 'Limelight', file: '/fonts/Limelight-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Minion Pro', file: '/fonts/MinionPro-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Mochesa', file: '/fonts/mochesa.woff2', ...REGULAR_ONLY },
  { name: 'Monoton', file: '/fonts/Monoton.woff2', ...REGULAR_ONLY },
  { name: 'Parisienne', file: '/fonts/Parisienne.woff2', ...REGULAR_ONLY },
  { name: 'Park Lane NF', system: true, ...REGULAR_ONLY },
  { name: 'Patrick Hand', file: '/fonts/PatrickHand-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Patua One', file: '/fonts/PatuaOne-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Permanent Marker', file: '/fonts/PermanentMarker-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Petit Formal Script', file: '/fonts/PetitFormalScript-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Playfair Display', file: '/fonts/PlayfairDisplay-Regular.woff2' },
  { name: 'Roboto', file: '/fonts/Roboto-Regular.woff2' },
  { name: 'Shadows Into Light', file: '/fonts/ShadowsIntoLight-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Titan One', file: '/fonts/TitanOne.woff2', ...REGULAR_ONLY },
  { name: 'Trebuchet MS', system: true },
  { name: 'Ultra', file: '/fonts/Ultra-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Yeseva One', file: '/fonts/YesevaOne-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Zagoth de Birse', file: '/fonts/Zagoth-de-Birse.woff2', ...REGULAR_ONLY },
  {name: 'Almendra', file: '/fonts/Almendra-Regular.woff2'},
  {name: 'Amatic SC', file: '/fonts/AmaticSC-Regular.woff2', noItalic: true},
  {name: 'Atma', file: '/fonts/Atma-Regular.woff2', noItalic: true},
  {name: 'Barlow Condensed', file: '/fonts/BarlowCondensed-Regular.woff2'},
  {name: 'Big Shoulders', file: '/fonts/BigShoulders-Regular.woff2', noItalic: true},
  {name: 'Bitcount Grid Double', file: '/fonts/BitcountGridDouble-Regular.woff2', noItalic: true},
  {name: 'Cabin', file: '/fonts/Cabin-Regular.woff2'},
  {name: 'Cabin Condensed', file: '/fonts/Cabin_Condensed-Regular.woff2'},
  {name: 'Caveat', file: '/fonts/Caveat-Regular.woff2', noItalic: true},
  {name: 'Cinzel Decorative', file: '/fonts/CinzelDecorative-Regular.woff2', noItalic: true},
  {name: 'ClementePDai', file: '/fonts/ClementePDai-Regular.woff2'},
  {name: 'Comic Neue', file: '/fonts/ComicNeue-Regular.woff2'},
  {name: 'Cormorant', file: '/fonts/Cormorant-Regular.woff2'},
  {name: 'Courier Prime', file: '/fonts/CourierPrime-Regular.woff2'},
  {name: 'Dancing Srcipt', file: '/fonts/DancingScript-Regular.woff2', noItalic: true},
  {name: 'Exo 2', file: '/fonts/Exo2-Regular.woff2'},
  {name: 'Fira Sans', file: '/fonts/FiraSans-Regular.woff2'},
  {name: 'Fira Sans Condensed', file: '/fonts/FiraSansCondensed-Regular.woff2'},
  {name: 'Foldit', file: '/fonts/Foldit-Regular.woff2', noItalic: true},
  {name: 'Fraunces 72pt', file: '/fonts/Fraunces_72pt-Regular.woff2'},
  {name: 'Fredoka', file: '/fonts/Fredoka-Regular.woff2', noItalic: true},
  {name: 'Fredoka Condensed', file: '/fonts/Fredoka_Condensed-Regular.woff2', noItalic: true},
  {name: 'Gluten', file: '/fonts/Gluten-Regular.woff2', noItalic: true},
  {name: 'IBM Plex Serif', file: '/fonts/IBMPlexSerif-Regular.woff2'},
  {name: 'Kalam', file: '/fonts/Kalam-Regular.woff2', noItalic: true},
  {name: 'Libre Baskerville', file: '/fonts/LibreBaskerville-Regular.woff2'},
  {name: 'Libre Franklin', file: '/fonts/LibreFranklin-Regular.woff2'},
  {name: 'Marhey', file: '/fonts/Marhey-Regular.woff2', noItalic: true},
  {name: 'Montserrat', file: '/fonts/Montserrat-Regular.woff2'},
  {name: 'Open sans', file: '/fonts/OpenSans-Regular.woff2'},
  {name: 'Open sans Condensed', file: '/fonts/OpenSans_Condensed-Regular.woff2'},
  {name: 'Passion One', file: '/fonts/PassionOne-Regular.woff2', noItalic: true},
  {name: 'Pixelify sans', file: '/fonts/PixelifySans-Regular.woff2', noItalic: true},
  {name: 'Quantico', file: '/fonts/Quantico-Regular.woff2'},
  {name: 'Roboto Condensed', file: '/fonts/Roboto_Condensed-Regular.woff2'},
  {name: 'Saira Stencil', file: '/fonts/SairaStencil-Regular.woff2'},
  {name: 'Saira Stencil Condensed', file: '/fonts/SairaStencil_Condensed-Regular.woff2'},
  {name: 'Science Gothic', file: '/fonts/ScienceGothic-Regular.woff2', noItalic: true},
  {name: 'Shantell Sans', file: '/fonts/ShantellSans-Regular.woff2'},
  {name: 'Stack Sans Notch', file: '/fonts/StackSansNotch-Regular.woff2', noItalic: true},
  {name: 'Tourney', file: '/fonts/Tourney-Regular.woff2'},
  {name: 'Zilla Slab', file: '/fonts/ZillaSlab-Regular.woff2'}
];

export const FONT_NAMES: string[] = FONTS.map((f) => f.name).sort((a, b) =>
  a.localeCompare(b, 'fr', { sensitivity: 'base' })
);

const FONT_BY_NAME = new Map(FONTS.map((f) => [f.name, f]));
const loaded = new Set<string>();

/** La police supporte-t-elle le gras ? (défaut : oui pour les polices inconnues) */
export function fontAllowsBold(name?: string | null): boolean {
  const def = name ? FONT_BY_NAME.get(name) : undefined;
  return !def?.noBold;
}

/** La police supporte-t-elle l'italique ? (défaut : oui pour les polices inconnues) */
export function fontAllowsItalic(name?: string | null): boolean {
  const def = name ? FONT_BY_NAME.get(name) : undefined;
  return !def?.noItalic;
}

/** Charge (une seule fois) la police demandée. No-op pour les polices système/Google. */
export function ensureFontLoaded(name?: string | null): void {
  if (!name || loaded.has(name)) return;
  const def = FONT_BY_NAME.get(name);
  if (!def || !def.file) {
    loaded.add(name);
    return;
  }
  loaded.add(name);
  if (typeof window === 'undefined' || typeof FontFace === 'undefined' || !document.fonts) return;
  try {
    const face = new FontFace(name, `url(${def.file}) format('woff2')`, { display: 'swap' });
    face.load().then((f) => document.fonts.add(f)).catch(() => {
      loaded.delete(name);
    });
  } catch {
    loaded.delete(name);
  }
}

/** Précharge en une passe une liste de polices (doublons ignorés). */
export function ensureFontsLoaded(names: (string | undefined | null)[]): void {
  for (const n of names) ensureFontLoaded(n);
}